import { useMemo } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { Visualizer } from '@urbanmix-tech/um-embedded';
import { getLegendConfig } from './visualizerConfig';

export interface LegendConfig {
  legendTitle?: string;
  dataType?: 'currency' | 'number' | 'percentage' | 'text';
  precision?: number;
  currencySymbol?: string;
  suffix?: string;
  showUnusedCategories?: boolean;
}

type BuildingLegendProps = {
  visualizer?: Visualizer;
  config?: LegendConfig;
};

interface LegendItem {
  color: string;
  label: string;
  min?: number;
  max?: number;
}

export default function BuildingLegend({
  visualizer,
  config = {},
}: BuildingLegendProps) {
  // Get config from visualizer if available, fallback to prop config
  const visualizerConfig = visualizer ? getLegendConfig(visualizer) : undefined;
  const mergedConfig = { ...visualizerConfig, ...config };

  const {
    legendTitle = 'LEGEND',
    dataType = 'text',
    precision = 0,
    currencySymbol = '$',
    suffix = '',
    showUnusedCategories = true,
  } = mergedConfig;

  // Helper function to format values based on dataType
  const formatValue = useMemo(() => {
    return (value: number): string => {
      let formatted = '';

      if (dataType === 'currency') {
        formatted = `${currencySymbol}${value.toFixed(precision)}`;
      } else if (dataType === 'percentage') {
        formatted = `${value.toFixed(precision)}%`;
      } else {
        formatted = value.toFixed(precision);
      }

      return suffix ? `${formatted}${suffix}` : formatted;
    };
  }, [dataType, precision, currencySymbol, suffix]);

  const legendItems = useMemo(() => {
    if (!visualizer) return [];

    const items: LegendItem[] = [];
    const vis = visualizer as any;

    // Check if it's a discrete/categorical visualizer (has colorMap)
    if (vis.colorMap instanceof Map) {
      // Discrete value visualizer - iterate through the color map
      vis.colorMap.forEach((color: string, value: any) => {
        items.push({
          color,
          label: String(value),
        });
      });
    } else if (
      vis.minValue !== undefined &&
      vis.maxValue !== undefined &&
      Array.isArray(vis.colors)
    ) {
      // Range visualizer (ColorGrade or ColorGradient)
      const minValue = vis.minValue;
      const maxValue = vis.maxValue;
      const colors = vis.colors;
      const numSegments = colors.length;
      const step = (maxValue - minValue) / Math.max(numSegments - 1, 1);

      colors.forEach((color: string, index: number) => {
        const segmentMin = minValue + step * index;
        const segmentMax = minValue + step * (index + 1);

        // Format the label as a range using formatValue for numeric types
        const minStr = dataType === 'text'
          ? (Number.isInteger(segmentMin)
              ? String(Math.round(segmentMin))
              : segmentMin.toFixed(1))
          : formatValue(segmentMin);

        const maxStr = dataType === 'text'
          ? (Number.isInteger(segmentMax)
              ? String(Math.round(segmentMax))
              : segmentMax.toFixed(1))
          : formatValue(segmentMax);

        items.push({
          color,
          label: `${minStr} - ${maxStr}`,
          min: segmentMin,
          max: segmentMax,
        });
      });

      // Sort range items in descending order (high values on top)
      items.sort((a, b) => (b.max ?? 0) - (a.max ?? 0));
    }

    return items;
  }, [visualizer, dataType, precision, currencySymbol, suffix, formatValue]);

  if (!visualizer || legendItems.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'absolute',
        bottom: 16,
        right: 16,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        backdropFilter: 'blur(4px)',
        borderRadius: '8px',
        padding: '12px 16px',
        maxWidth: '280px',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        zIndex: 10,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: 'rgba(255, 255, 255, 0.7)',
          fontWeight: 600,
          display: 'block',
          marginBottom: '8px',
          fontSize: '0.75rem',
          letterSpacing: '0.5px',
        }}
      >
        {legendTitle}
      </Typography>
      <Stack spacing={0.75}>
        {legendItems.map((item, index) => (
          <Stack
            key={`${item.color}-${index}`}
            direction="row"
            alignItems="center"
            spacing={0.75}
          >
            <Box
              sx={{
                width: 12,
                height: 12,
                backgroundColor: item.color,
                borderRadius: '2px',
                flexShrink: 0,
                border: '1px solid rgba(0, 0, 0, 0.3)',
              }}
            />
            <Typography
              variant="caption"
              sx={{
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: '0.7rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '200px',
              }}
            >
              {item.label}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}
